import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
import { Button } from 'src/@/components/ui/button';
import Iconify from 'src/components/Iconify';

export default function MktPostDeleteDialog({ open, post, onClose, onConfirm, deleting }) {
    const { t } = useTranslation('marketing');
    const [remote, setRemote] = useState(true);

    const hasFacebook = post?.platforms?.includes('facebook') && post?.external_ids?.facebook?.post_id;
    const hasInstagram = post?.platforms?.includes('instagram') && post?.external_ids?.instagram?.media_id;
    const isPublished = post?.status === 'published' || post?.status === 'partially_published';

    const previewText = post?.content?.text
        ? `${post.content.text.slice(0, 60)}${post.content.text.length > 60 ? '...' : ''}`
        : null;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="sm:max-w-[440px] rounded-2xl border-0 shadow-2xl bg-white p-0 overflow-hidden">
                <div className="p-6 pb-4 border-b border-gray-100 bg-red-50/50">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                            <Iconify icon="mdi:alert-circle-outline" width={24} height={24} className="text-red-500" />
                            {t('posts.delete.title')}
                        </DialogTitle>
                    </DialogHeader>
                </div>

                <div className="p-6">
                    <p className="text-gray-600 text-sm">
                        {previewText
                            ? t('posts.delete.confirmNamed', { text: previewText })
                            : t('posts.delete.confirm')}
                    </p>

                    {isPublished && (hasFacebook || hasInstagram) && (
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 space-y-1">
                            {hasFacebook && (
                                <p>
                                    {t('posts.delete.facebookRemoveNote', {
                                        remote: remote ? t('posts.delete.facebookRemote') : ''
                                    })}
                                </p>
                            )}
                            {hasInstagram && (
                                <p>{t('posts.delete.instagramManualDelete')}</p>
                            )}
                        </div>
                    )}

                    {isPublished && hasFacebook && (
                        <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                className="w-4 h-4 text-[#7C3AED] border-gray-300 rounded"
                                checked={remote}
                                onChange={(e) => setRemote(e.target.checked)}
                            />
                            {t('posts.delete.alsoFromFacebook')}
                        </label>
                    )}

                    <p className="text-xs text-gray-400 mt-3">{t('posts.delete.warning')}</p>
                </div>

                <div className="flex justify-end gap-3 p-6 pt-2 border-t border-gray-100 bg-gray-50/30">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        disabled={deleting}
                        className="rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={() => onConfirm({ remote: Boolean(isPublished && hasFacebook && remote) })}
                        disabled={deleting}
                        className="bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-sm px-6"
                    >
                        {deleting ? (
                            <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white" />
                                {t('common.deleting')}
                            </div>
                        ) : (
                            t('posts.delete.deletePost')
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
